import { useCallback } from 'react';

import { graphql, useMutation } from 'react-relay';
import type { PayloadError } from 'relay-runtime';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type {
	useInvokeAgentMutation as useInvokeAgentMutationType,
	useInvokeAgentMutation$data,
} from './__generated__/useInvokeAgentMutation.graphql';

type InvokeAgentArgs = {
	cloudId: string;
	issueId: string;
	agentId: string | null;
	identityAccountId: string;
};

type UseInvokeAgentOptions = {
	conversationId: string | undefined;
	invocationId: string | undefined;
	messageId: string | undefined;
};

export const useInvokeAgent = ({
	conversationId,
	invocationId,
	messageId,
}: UseInvokeAgentOptions): {
	invokeAgent: (args: InvokeAgentArgs) => Promise<useInvokeAgentMutation$data>;
	isInvoking: boolean;
} => {
	const { sendAnalyticsEvent } = useAnalytics();

	const [commit, isInvoking] = useMutation<useInvokeAgentMutationType>(graphql`
		mutation useInvokeAgentMutation($input: JiraStartAiAgentSessionInput!) {
			jira_startAiAgentSession(input: $input) {
				success
				errors {
					message
					extensions {
						statusCode
						errorType
					}
				}
			}
		}
	`);

	const invokeAgent = useCallback(
		({
			cloudId,
			issueId,
			agentId,
			identityAccountId,
		}: InvokeAgentArgs): Promise<useInvokeAgentMutation$data> =>
			new Promise((resolve, reject) => {
				const trackFailure = () => {
					sendAnalyticsEvent({
						eventType: 'track',
						actionSubject: 'invokeAgent',
						action: 'failed',
						attributes: {
							analyticsSource: 'inline-create-agent-card',
							conversationId,
							invocationId,
							messageId,
							agentId,
							issueId,
						},
					});
				};
				commit({
					variables: {
						input: {
							agentId: identityAccountId,
							// alternatively we could consider swaping with uuid() package similar to Jira
							clientMutationId: crypto.randomUUID(),
							cloudId,
							issueId,
						},
					},
					onCompleted: (response, errors: PayloadError[] | null | undefined) => {
						if (errors && errors.length > 0) {
							trackFailure();
							reject(new Error(errors.map((e) => e.message).join(', ')));
							return;
						}
						if (!response.jira_startAiAgentSession?.success) {
							const message =
								response.jira_startAiAgentSession?.errors?.[0]?.message ?? 'Failed to invoke agent';
							trackFailure();
							reject(new Error(message));
							return;
						}
						sendAnalyticsEvent({
							eventType: 'track',
							actionSubject: 'agentSession',
							action: 'triggered',
							attributes: {
								analyticsSource: 'inline-create-agent-card',
								conversationId,
								invocationId,
								messageId,
								agentId,
								issueId,
							},
						});
						resolve(response);
					},
					onError: (error) => {
						trackFailure();
						reject(error);
					},
				});
			}),
		[commit, sendAnalyticsEvent, conversationId, invocationId, messageId],
	);

	return { invokeAgent, isInvoking };
};
